'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Play, Pause, ChevronLeft, ChevronRight } from 'lucide-react';

type DemoSlide = {
  bg: string;
  render: () => React.ReactNode;
};

type Demo = {
  id: string;
  name: string;
  category: string;
  description: string;
  slideDuration: number; // ms
  slides: DemoSlide[];
};

const DEMOS: Demo[] = [
  // ─────────────────────────────
  // 1. Fastfood menu-bord
  // ─────────────────────────────
  {
    id: 'fastfood',
    name: 'Fastfood menu-bord',
    category: 'Horeca · Fastfood',
    description: 'McDonalds/KFC stijl met grote foto\'s, felle kleuren en prijzen in cirkels. Ideaal voor afhaalhoek.',
    slideDuration: 4000,
    slides: [
      {
        bg: '#c41e3a',
        render: () => (
          <div className="relative w-full h-full overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[10%] bg-[#ffd400] flex items-center justify-between px-[3%]">
              <div className="text-[#c41e3a] font-black text-[clamp(10px,2vw,28px)]">BURGERS</div>
              <div className="text-[#c41e3a] font-black text-[clamp(8px,1.4vw,18px)]">PUUR · VERS · LEKKER</div>
            </div>
            <div className="absolute top-[15%] left-[4%] w-[20%] h-[35%] bg-[#222] rounded-lg overflow-hidden">
              <img src="https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&q=80" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute top-[18%] left-[26%] text-white font-black text-[clamp(14px,3.2vw,52px)] leading-none">CLASSIC<br/>BURGER</div>
            <div className="absolute top-[35%] left-[26%] text-[#ffd400] text-[clamp(8px,1.4vw,22px)]">100% rundvlees, cheddar, sla</div>
            <div className="absolute top-[18%] right-[4%] w-[15%] aspect-square bg-[#ffd400] rounded-full flex items-center justify-center">
              <span className="text-[#c41e3a] font-black text-[clamp(12px,2.6vw,42px)]">€8,95</span>
            </div>
            <div className="absolute top-[55%] left-[4%] w-[20%] h-[35%] bg-[#222] rounded-lg overflow-hidden">
              <img src="https://images.unsplash.com/photo-1572802419224-296b0aeee0d9?w=400&q=80" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute top-[58%] left-[26%] text-white font-black text-[clamp(14px,3.2vw,52px)] leading-none">DOUBLE<br/>CHEESE</div>
            <div className="absolute top-[75%] left-[26%] text-[#ffd400] text-[clamp(8px,1.4vw,22px)]">Dubbel rundvlees, dubbel cheddar</div>
            <div className="absolute top-[58%] right-[4%] w-[15%] aspect-square bg-[#ffd400] rounded-full flex items-center justify-center">
              <span className="text-[#c41e3a] font-black text-[clamp(12px,2.4vw,38px)]">€10,95</span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-[8%] bg-[#ffd400] flex items-center justify-center">
              <span className="text-[#c41e3a] font-black text-[clamp(10px,2vw,28px)]">+ MAAK ER EEN MENU VAN — €4,50</span>
            </div>
          </div>
        ),
      },
      {
        bg: '#ffd400',
        render: () => (
          <div className="relative w-full h-full overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[10%] bg-[#c41e3a] flex items-center px-[3%]">
              <div className="text-[#ffd400] font-black text-[clamp(10px,2vw,28px)]">MENU'S — VOORDEEL COMBO</div>
            </div>
            {[
              { img: 'https://images.unsplash.com/photo-1606131731446-5568d87113aa?w=500&q=80', title: 'BURGER COMBO', sub: 'Burger + frites + drank', price: '€12,95' },
              { img: 'https://images.unsplash.com/photo-1561758033-d89a9ad46330?w=500&q=80', title: 'FAMILY DEAL', sub: '4× burgers + frites + drank', price: '€34,95' },
              { img: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=500&q=80', title: 'VEGGIE COMBO', sub: 'Veggie burger + frites + drank', price: '€11,95' },
            ].map((m, i) => (
              <div key={i} className="absolute top-[15%] h-[80%] flex flex-col items-center" style={{ left: `${4 + i * 32}%`, width: '28%' }}>
                <div className="w-full h-[58%] bg-[#222] rounded-lg overflow-hidden">
                  <img src={m.img} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="text-[#c41e3a] font-black text-[clamp(10px,2vw,30px)] mt-[3%]">{m.title}</div>
                <div className="text-[#0f1115] text-[clamp(7px,1.2vw,18px)] mt-[1%] text-center">{m.sub}</div>
                <div className="bg-[#c41e3a] text-[#ffd400] font-black text-[clamp(11px,2.2vw,34px)] px-[12%] py-[3%] rounded-full mt-auto">
                  {m.price}
                </div>
              </div>
            ))}
          </div>
        ),
      },
      {
        bg: '#0d7c66',
        render: () => (
          <div className="relative w-full h-full overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[10%] bg-[#ffd400] flex items-center px-[3%]">
              <div className="text-[#c41e3a] font-black text-[clamp(10px,2vw,28px)]">DRINKS & SIDES</div>
            </div>
            {[
              { img: 'https://images.unsplash.com/photo-1573080496219-bb080dd4f877?w=400&q=80', title: 'FRITES', price: '€3,50' },
              { img: 'https://images.unsplash.com/photo-1639024471283-03518883512d?w=400&q=80', title: 'UIRINGEN', price: '€4,25' },
              { img: 'https://images.unsplash.com/photo-1554866585-cd94860890b7?w=400&q=80', title: 'FRISDRANK', price: '€2,95' },
              { img: 'https://images.unsplash.com/photo-1572490122747-3968b75cc699?w=400&q=80', title: 'MILKSHAKE', price: '€4,50' },
            ].map((m, i) => (
              <div key={i} className="absolute top-[15%] h-[70%] flex flex-col items-center" style={{ left: `${3 + i * 24.5}%`, width: '21%' }}>
                <div className="w-full h-[60%] bg-[#222] rounded-lg overflow-hidden">
                  <img src={m.img} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="text-[#ffd400] font-black text-[clamp(11px,2.2vw,32px)] mt-[5%]">{m.title}</div>
                <div className="bg-[#ffd400] text-[#0d7c66] font-black text-[clamp(10px,2vw,28px)] px-[12%] py-[2%] rounded-full mt-[4%]">
                  {m.price}
                </div>
              </div>
            ))}
            <div className="absolute bottom-0 left-0 right-0 h-[10%] bg-[#ffd400] flex items-center justify-center">
              <span className="text-[#c41e3a] font-black text-[clamp(12px,2.4vw,36px)]">XL VOOR €1 EXTRA</span>
            </div>
          </div>
        ),
      },
    ],
  },

  // ─────────────────────────────
  // 2. Restaurant lunchmenu (uit bestaande template)
  // ─────────────────────────────
  {
    id: 'restaurant',
    name: 'Restaurant lunchmenu',
    category: 'Horeca · Restaurant',
    description: 'Klassiek en elegant. 3 slides die roteren tussen voor-, hoofd- en specials. Geschikt voor bistros en cafés.',
    slideDuration: 4500,
    slides: [
      {
        bg: '#fafaf7',
        render: () => (
          <div className="relative w-full h-full px-[6%] py-[8%]">
            <div className="text-[#0d7c66] font-black tracking-tight text-[clamp(18px,4.2vw,72px)] leading-none">VOORGERECHTEN</div>
            <div className="bg-[#ff6b4a] h-[3px] w-full mt-[2%]"></div>
            <div className="mt-[5%] space-y-[3%]">
              <div className="text-[#0f1115] text-[clamp(11px,2.4vw,36px)]">Soep van de dag — 6,50</div>
              <div className="text-[#0f1115] text-[clamp(11px,2.4vw,36px)]">Carpaccio met truffel — 12,50</div>
              <div className="text-[#0f1115] text-[clamp(11px,2.4vw,36px)]">Bruschetta met tomaat — 9,00</div>
            </div>
            <div className="absolute bottom-[6%] left-[6%] text-[#5a5d68] text-[clamp(8px,1.4vw,20px)]">
              Geserveerd tussen 12:00 en 16:00
            </div>
          </div>
        ),
      },
      {
        bg: '#fafaf7',
        render: () => (
          <div className="relative w-full h-full px-[6%] py-[8%]">
            <div className="text-[#0d7c66] font-black tracking-tight text-[clamp(18px,4.2vw,72px)] leading-none">HOOFDGERECHTEN</div>
            <div className="bg-[#ff6b4a] h-[3px] w-full mt-[2%]"></div>
            <div className="mt-[5%] space-y-[3%]">
              <div className="text-[#0f1115] text-[clamp(11px,2.4vw,36px)]">Tournedos van de grill — 24,50</div>
              <div className="text-[#0f1115] text-[clamp(11px,2.4vw,36px)]">Zalmfilet met groenten — 22,00</div>
              <div className="text-[#0f1115] text-[clamp(11px,2.4vw,36px)]">Risotto met paddenstoelen — 18,50</div>
            </div>
          </div>
        ),
      },
      {
        bg: '#0d7c66',
        render: () => (
          <div className="relative w-full h-full flex flex-col items-center justify-center text-center px-[6%]">
            <div className="text-[#ffd86b] font-black text-[clamp(14px,3vw,52px)] tracking-wide">SPECIAL VAN DE WEEK</div>
            <div className="text-white text-[clamp(11px,2.5vw,42px)] mt-[3%]">Gegrilde lamsrack met rozemarijn</div>
            <div className="text-[#ff6b4a] font-black text-[clamp(20px,4.5vw,80px)] mt-[3%]">€ 28,50</div>
          </div>
        ),
      },
    ],
  },

  // ─────────────────────────────
  // 3. Kapper wachtkamer
  // ─────────────────────────────
  {
    id: 'kapper',
    name: 'Kapper wachtkamer',
    category: 'Service · Kapper',
    description: 'Wachtrij + tarieven + welkom rouleren. Klanten zien hun nummer op het scherm én op de telefoon.',
    slideDuration: 5000,
    slides: [
      {
        bg: '#1a1d24',
        render: () => (
          <div className="relative w-full h-full p-[3%] flex flex-col">
            <div className="flex justify-between items-baseline">
              <div className="text-white/60 font-mono text-[clamp(7px,1.2vw,16px)] uppercase tracking-widest">Wachtrij</div>
              <div className="text-white/60 text-[clamp(7px,1.2vw,16px)] bg-white/10 px-[1.5%] py-[0.5%] rounded">8 wachtenden</div>
            </div>
            <div className="bg-[#0d7c66]/40 mt-[2%] p-[3%] rounded-lg flex items-center gap-[3%]">
              <div className="bg-white/30 text-white w-[12%] aspect-square rounded-full flex items-center justify-center font-black text-[clamp(14px,3vw,48px)]">→</div>
              <div className="flex-1">
                <div className="text-white/80 font-mono text-[clamp(7px,1.2vw,16px)] uppercase tracking-widest">Wordt geroepen</div>
                <div className="text-white font-black text-[clamp(20px,4.5vw,80px)] leading-none mt-[1%]">A24 · Lisa</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-[2%] mt-[3%]">
              <div className="bg-white/10 p-[5%] rounded-lg text-white">
                <div className="text-[clamp(6px,1.1vw,14px)] opacity-70 font-mono uppercase">Volgende</div>
                <div className="font-black text-[clamp(12px,2.4vw,32px)] mt-[3%]">A25 · Marco</div>
              </div>
              <div className="bg-white/5 p-[5%] rounded-lg text-white">
                <div className="text-[clamp(6px,1.1vw,14px)] opacity-60 font-mono uppercase">Daarna</div>
                <div className="font-black text-[clamp(12px,2.4vw,32px)] mt-[3%]">A26 · Anita</div>
              </div>
              <div className="bg-white/5 p-[5%] rounded-lg text-white">
                <div className="text-[clamp(6px,1.1vw,14px)] opacity-60 font-mono uppercase">Wachttijd</div>
                <div className="font-black text-[clamp(12px,2.4vw,32px)] mt-[3%]">±18 min</div>
              </div>
            </div>
            <div className="mt-auto bg-[#ff6b4a]/15 border border-[#ff6b4a]/30 p-[3%] rounded-lg">
              <div className="text-[#ff6b4a] font-mono text-[clamp(7px,1.2vw,16px)] uppercase tracking-widest mb-[1%]">Service van de week</div>
              <div className="text-white text-[clamp(10px,2vw,28px)]">Highlights vanaf €70 — boek bij je volgende bezoek</div>
            </div>
          </div>
        ),
      },
      {
        bg: '#fafaf7',
        render: () => (
          <div className="relative w-full h-full px-[6%] py-[8%]">
            <div className="text-[#0d7c66] font-black text-[clamp(18px,4vw,68px)] leading-none">TARIEVEN</div>
            <div className="bg-[#ff6b4a] h-[3px] w-full mt-[2%]"></div>
            <div className="mt-[4%] space-y-[2.5%]">
              <Tarief naam="Knippen" prijs="€ 22,50" />
              <Tarief naam="Wassen + knippen" prijs="€ 29,00" />
              <Tarief naam="Wassen + knippen + föhnen" prijs="€ 34,50" />
              <Tarief naam="Kleuren (per pakket)" prijs="vanaf € 65,00" />
              <Tarief naam="Highlights" prijs="vanaf € 70,00" />
              <Tarief naam="Behandeling kinderen (t/m 12)" prijs="€ 17,50" />
            </div>
          </div>
        ),
      },
      {
        bg: '#fafaf7',
        render: () => (
          <div className="relative w-full h-full flex flex-col items-center justify-center text-center px-[6%]">
            <div className="text-[#0d7c66] font-black text-[clamp(28px,7vw,140px)] tracking-tight leading-none">Welkom</div>
            <div className="text-[#5a5d68] text-[clamp(10px,2.2vw,36px)] mt-[3%]">Fijn dat je er bent</div>
            <div className="bg-[#ffd86b]/40 px-[4%] py-[2%] rounded mt-[5%] text-[#5a5d68] font-mono text-[clamp(9px,1.6vw,22px)]">
              {new Date().toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
          </div>
        ),
      },
    ],
  },

  // ─────────────────────────────
  // 4. Retail promotie
  // ─────────────────────────────
  {
    id: 'retail',
    name: 'Retail promotie',
    category: 'Retail · Mode',
    description: 'Aanbiedingen, openingstijden en social proof in 1 cyclus. Trek aandacht in de etalage of kassa-zone.',
    slideDuration: 4500,
    slides: [
      {
        bg: '#000000',
        render: () => (
          <div className="relative w-full h-full flex flex-col items-center justify-center text-center px-[6%]">
            <div className="text-[#ff6b4a] font-mono text-[clamp(8px,1.6vw,22px)] uppercase tracking-[0.3em] mb-[2%]">SALE</div>
            <div className="text-white font-black text-[clamp(40px,10vw,200px)] tracking-tight leading-none">-50%</div>
            <div className="text-white/80 text-[clamp(10px,2.2vw,32px)] mt-[3%]">Op de hele winterselectie</div>
            <div className="bg-white text-black font-black px-[4%] py-[1.5%] mt-[5%] text-[clamp(9px,1.8vw,24px)]">
              ALLEEN DEZE WEEK
            </div>
          </div>
        ),
      },
      {
        bg: '#fafaf7',
        render: () => (
          <div className="relative w-full h-full p-[6%] grid grid-cols-2 gap-[4%]">
            <div className="flex flex-col justify-center">
              <div className="font-mono text-[clamp(7px,1.2vw,16px)] uppercase tracking-[0.2em] text-[#0d7c66] mb-[3%]">— REVIEWS</div>
              <div className="text-[clamp(40px,8vw,140px)] font-black text-[#0f1115] leading-none">4,8</div>
              <div className="text-[#ffa726] text-[clamp(14px,3vw,42px)] mt-[2%]">★★★★★</div>
              <div className="text-[#5a5d68] text-[clamp(8px,1.5vw,20px)] mt-[2%]">Op basis van 248 reviews</div>
            </div>
            <div className="bg-white border border-[#e5e5dd] rounded-lg p-[5%] flex flex-col justify-center">
              <div className="text-[#ffa726] text-[clamp(12px,2.4vw,32px)]">★★★★★</div>
              <div className="italic text-[#0f1115] text-[clamp(10px,2vw,28px)] mt-[3%] leading-snug">
                "Geweldige service en perfecte pasvorm. Kom zeker terug!"
              </div>
              <div className="text-[#5a5d68] text-[clamp(7px,1.4vw,18px)] mt-[3%]">— Marieke V.</div>
            </div>
          </div>
        ),
      },
      {
        bg: '#0d7c66',
        render: () => (
          <div className="relative w-full h-full flex flex-col items-center justify-center text-center px-[6%]">
            <div className="text-[#ffd86b] font-mono text-[clamp(8px,1.6vw,22px)] uppercase tracking-[0.3em] mb-[3%]">— OPENINGSTIJDEN</div>
            <div className="text-white space-y-[1.5%] text-[clamp(11px,2.4vw,36px)]">
              <div className="flex justify-between gap-[20%]"><span>Ma — Wo</span><span className="font-bold">10:00 — 18:00</span></div>
              <div className="flex justify-between gap-[20%]"><span>Do</span><span className="font-bold">10:00 — 21:00</span></div>
              <div className="flex justify-between gap-[20%]"><span>Vr — Za</span><span className="font-bold">10:00 — 18:00</span></div>
              <div className="flex justify-between gap-[20%]"><span>Zo</span><span className="font-bold text-[#ffd86b]">12:00 — 17:00</span></div>
            </div>
          </div>
        ),
      },
    ],
  },

  // ─────────────────────────────
  // 5. Kantoor / receptie
  // ─────────────────────────────
  {
    id: 'kantoor',
    name: 'Kantoor receptie',
    category: 'Bedrijf · Receptie',
    description: 'Welkom bezoekers, agenda van de dag, en bedrijfsmededelingen. Combineer met klok en weer.',
    slideDuration: 5000,
    slides: [
      {
        bg: '#fafaf7',
        render: () => (
          <div className="relative w-full h-full p-[6%] grid grid-cols-2 gap-[5%]">
            <div className="flex flex-col justify-center">
              <div className="font-mono text-[clamp(7px,1.2vw,16px)] uppercase tracking-[0.2em] text-[#0d7c66] mb-[3%]">— VANDAAG</div>
              <div className="text-[#0f1115] text-[clamp(28px,5vw,90px)] font-black leading-none">
                {new Date().toLocaleDateString('nl-NL', { weekday: 'long' })}
              </div>
              <div className="text-[#5a5d68] text-[clamp(10px,2vw,28px)] mt-[2%]">
                {new Date().toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
              </div>
            </div>
            <div className="flex flex-col gap-[5%] justify-center">
              <div className="bg-white border border-[#e5e5dd] rounded-lg p-[6%]">
                <div className="text-[clamp(7px,1.2vw,16px)] font-mono uppercase tracking-widest text-[#5a5d68]">Tijd</div>
                <Clock />
              </div>
              <div className="bg-[#0d7c66] text-white rounded-lg p-[6%] flex items-center gap-[5%]">
                <div className="text-[clamp(20px,4vw,72px)]">☀️</div>
                <div>
                  <div className="text-[clamp(14px,3vw,48px)] font-black leading-none">19°</div>
                  <div className="text-[clamp(8px,1.4vw,18px)] opacity-80">Zonnig · Rotterdam</div>
                </div>
              </div>
            </div>
          </div>
        ),
      },
      {
        bg: '#fafaf7',
        render: () => (
          <div className="relative w-full h-full px-[6%] py-[6%]">
            <div className="text-[#0d7c66] font-black text-[clamp(16px,3.6vw,60px)] leading-none">Welkom bezoekers</div>
            <div className="bg-[#ff6b4a] h-[3px] w-[15%] mt-[2%]"></div>
            <div className="mt-[5%] space-y-[2%]">
              {[
                { tijd: '09:00', naam: 'Maria van der Berg', host: '— Bezoekt: Pieter Jansen' },
                { tijd: '10:30', naam: 'BCD Holding B.V.', host: '— Bezoekt: Sales team' },
                { tijd: '14:00', naam: 'TechCorp Solutions', host: '— Bezoekt: Engineering' },
              ].map((m, i) => (
                <div key={i} className="bg-white border border-[#e5e5dd] rounded-lg p-[3%] flex items-center gap-[3%]">
                  <div className="bg-[#0d7c66] text-white px-[2%] py-[1%] font-mono font-bold rounded text-[clamp(9px,1.8vw,24px)]">{m.tijd}</div>
                  <div>
                    <div className="text-[#0f1115] font-bold text-[clamp(10px,2vw,28px)]">{m.naam}</div>
                    <div className="text-[#5a5d68] text-[clamp(7px,1.3vw,16px)]">{m.host}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        bg: '#0d7c66',
        render: () => (
          <div className="relative w-full h-full flex flex-col items-center justify-center text-center px-[6%]">
            <div className="text-[#ffd86b] font-mono text-[clamp(8px,1.6vw,22px)] uppercase tracking-[0.3em] mb-[3%]">— MEDEDELING</div>
            <div className="text-white font-black text-[clamp(16px,3.6vw,60px)] leading-tight">
              Aangepaste<br/>openingstijden
            </div>
            <div className="text-white/80 text-[clamp(10px,2vw,28px)] mt-[3%]">
              Vrijdag 24 mei zijn wij gesloten i.v.m. Hemelvaart
            </div>
          </div>
        ),
      },
    ],
  },
];

function Clock() {
  const [time, setTime] = useState<string>('--:--');
  useEffect(() => {
    function update() {
      setTime(new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }));
    }
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, []);
  return <div className="text-[#0f1115] font-black text-[clamp(20px,4vw,72px)] font-mono leading-none mt-[2%]">{time}</div>;
}

function Tarief({ naam, prijs }: { naam: string; prijs: string }) {
  return (
    <div className="flex justify-between items-baseline gap-[3%]">
      <span className="text-[#0f1115] text-[clamp(9px,1.9vw,28px)]">{naam}</span>
      <div className="flex-1 border-b border-dotted border-[#5a5d68]/40 mb-[0.4%]"></div>
      <span className="text-[#0d7c66] font-bold text-[clamp(9px,1.9vw,28px)]">{prijs}</span>
    </div>
  );
}

export function DemoView() {
  const [demoIdx, setDemoIdx] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const slideStartRef = useRef(Date.now());
  const [progress, setProgress] = useState(0);

  const demo = DEMOS[demoIdx];
  const slide = demo.slides[slideIdx];

  // Auto-advance slides
  useEffect(() => {
    if (!isPlaying) return;
    slideStartRef.current = Date.now();
    setProgress(0);
    const tick = setInterval(() => {
      const elapsed = Date.now() - slideStartRef.current;
      const pct = Math.min(100, (elapsed / demo.slideDuration) * 100);
      setProgress(pct);
      if (elapsed >= demo.slideDuration) {
        setSlideIdx((i) => (i + 1) % demo.slides.length);
      }
    }, 50);
    return () => clearInterval(tick);
  }, [isPlaying, slideIdx, demoIdx, demo.slideDuration, demo.slides.length]);

  // Reset to first slide when demo changes
  useEffect(() => {
    setSlideIdx(0);
  }, [demoIdx]);

  function selectDemo(i: number) {
    setDemoIdx(i);
  }

  function prevSlide() {
    setSlideIdx((i) => (i - 1 + demo.slides.length) % demo.slides.length);
  }

  function nextSlide() {
    setSlideIdx((i) => (i + 1) % demo.slides.length);
  }

  return (
    <div className="bg-bg">
      <section className="max-w-[1200px] mx-auto px-6 pt-16 pb-8">
        <div className="text-center max-w-2xl mx-auto">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary font-medium">
            — Live demo
          </span>
          <h1 className="text-4xl md:text-5xl font-bold mt-4 leading-[1.05] tracking-tight">
            Zo ziet Castly er live uit.
          </h1>
          <p className="text-base text-ink-2 mt-5 leading-relaxed">
            Bekijk hieronder hoe verschillende presentaties op een echt scherm draaien. Dit zijn
            kant-en-klare templates die je in 5 minuten aanpast aan je eigen zaak.
          </p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 pb-12">
        {/* TV frame */}
        <div className="relative max-w-4xl mx-auto">
          <div className="bg-[#1a1d24] rounded-2xl p-3 md:p-4 shadow-2xl">
            <div
              className="w-full overflow-hidden rounded-md relative"
              style={{ aspectRatio: '16/9', background: slide.bg, transition: 'background 0.4s ease' }}
            >
              {slide.render()}
            </div>
            {/* TV chin */}
            <div className="flex items-center justify-between gap-3 px-1 pt-3 pb-1">
              <div className="flex items-center gap-2 text-white/50 text-[10px] font-mono">
                <span className="w-1.5 h-1.5 bg-success rounded-full animate-pulse"></span>
                LIVE PREVIEW
              </div>
              <div className="text-white/40 text-[10px] font-mono uppercase tracking-widest">
                {demo.category}
              </div>
            </div>
          </div>

          {/* TV stand */}
          <div className="mx-auto w-[15%] h-3 bg-[#1a1d24]/80 rounded-b-lg"></div>
          <div className="mx-auto w-[35%] h-2 bg-[#1a1d24]/60 rounded-full mt-1"></div>

          {/* Controls below TV */}
          <div className="mt-6 flex items-center justify-center gap-3">
            <button
              onClick={prevSlide}
              className="w-9 h-9 rounded-full border border-border bg-bg-2 hover:bg-bg-3 flex items-center justify-center"
              title="Vorige slide"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="w-11 h-11 rounded-full bg-primary text-white flex items-center justify-center hover:bg-primary-deep"
              title={isPlaying ? 'Pauzeer' : 'Hervat'}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
            </button>
            <button
              onClick={nextSlide}
              className="w-9 h-9 rounded-full border border-border bg-bg-2 hover:bg-bg-3 flex items-center justify-center"
              title="Volgende slide"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          {/* Slide indicators */}
          <div className="mt-4 flex items-center justify-center gap-1.5">
            {demo.slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setSlideIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === slideIdx ? 'w-8 bg-primary' : 'w-1.5 bg-ink-3/30 hover:bg-ink-3/50'
                }`}
                style={
                  i === slideIdx && isPlaying
                    ? {
                        background: `linear-gradient(to right, var(--primary) ${progress}%, var(--primary-soft) ${progress}%)`,
                      }
                    : {}
                }
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Demo selector */}
      <section className="max-w-[1200px] mx-auto px-6 pb-16">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold tracking-tight">Bekijk meer voorbeelden</h2>
          <p className="text-sm text-ink-2 mt-1">Klik om een ander template te bekijken.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {DEMOS.map((d, i) => (
            <button
              key={d.id}
              onClick={() => selectDemo(i)}
              className={`text-left p-4 rounded-lg border transition-colors ${
                i === demoIdx
                  ? 'border-primary bg-primary-soft'
                  : 'border-border bg-bg-2 hover:bg-bg-3 hover:border-border-2'
              }`}
            >
              <div className="font-mono text-[9px] uppercase tracking-widest text-ink-3 mb-1">
                {d.category}
              </div>
              <div className="font-bold text-sm">{d.name}</div>
              <div className="text-[12px] text-ink-2 mt-1 leading-snug">{d.description}</div>
              <div className="mt-3 text-[10px] text-ink-3 flex items-center gap-1.5">
                <span className="w-1 h-1 bg-ink-3 rounded-full"></span>
                {d.slides.length} slide{d.slides.length === 1 ? '' : 's'} · roteert elke{' '}
                {Math.round(d.slideDuration / 1000)}s
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1200px] mx-auto px-6 pb-20">
        <div className="bg-bg-2 border border-border rounded-xl p-8 md:p-12 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary font-medium mb-3">
            — Probeer het zelf
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Zo gemaakt. Zo aangepast.
          </h2>
          <p className="text-base text-ink-2 mt-4 max-w-md mx-auto">
            Alle templates die je hier ziet zijn beschikbaar in de editor. Pas tekst, kleuren en
            foto's aan in 5 minuten en publiceer naar je scherm.
          </p>
          <div className="mt-6 flex gap-2 justify-center flex-wrap">
            <Link href="/register" className="btn-primary">
              Start je 7 dagen trial <ArrowRight size={14} />
            </Link>
            <Link href="/integraties" className="btn-ghost">
              Bekijk integraties
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
